import { Component } from "../core/Component";
import Strings from "../core/data/Strings";
import { GameObject } from "../core/GameObject";
import NineSlicedSprite from "../core/NineSlicedSprite";
import ResizableContainer from "../core/ResizableContainer";
import ResizableScene from "../core/ResizableScene";
import GameColors from "../GameColors";
import Fonts from "../utils/Fonts";
import LayoutUtils from "../utils/LayoutUtils";

export type LoadingContainerState = {
    charactersTime: number;
    starsTime: number;
    linesTime: number[];
}

export default class LoadingContainer extends ResizableContainer {
    private _onResize: () => any;
    private _targetPercent = 0;
    private _percent = 0;
    private _timer = 0;
    loadMeter: Phaser.GameObjects.Container;
    meterFill: NineSlicedSprite;

    characters: SpineGameObject;
    stars: SpineGameObject;
    lines: GameObject[] = [];

    buildComplete: Promise<void>;
    static BUILD_COMPLETE = "buildComplete";

    get containerState(): LoadingContainerState {
        return {
            charactersTime: this.characters.state.tracks[0].trackTime,
            starsTime: this.stars.state.tracks[0].trackTime,
            linesTime: this.lines.map(l => l.getComponent(AlphaFade).timer),
        };
    }

    constructor(scene: ResizableScene, intro: boolean, showLoadMeter: boolean, state?: LoadingContainerState) {
        super(scene, 0, 0);
        this._targetPercent = 0.001;
        this._percent = 0.001;

        const loadBG = scene.add.image(0, 0, "splashBG");
        this.add(loadBG);

        const leftAssets = scene.add.container();
        this.add(leftAssets);

        const createLine = (x: number, y: number, key: string, offset: number) => {
            const obj = new GameObject(scene);
            obj.setPosition(x, y);
            obj.addImage(key);
            obj.addComponent(new AlphaFade(obj, offset));
            return obj;
        }

        const lines1 = createLine(1000, 160, "splashLines1", state ? state.linesTime[0] : 0);
        const lines2 = createLine(1700, 970, "splashLines2", state ? state.linesTime[1] : 0.2);
        const lines3 = createLine(250, 855, "splashLines3", state ? state.linesTime[2] : 0.4);
        const lines = this.lines = [lines1, lines2, lines3];

        const linesContainer = scene.add.container(600, 500);
        leftAssets.add(linesContainer);
        linesContainer.add(lines);
        lines.forEach(l => {
            l.x -= linesContainer.x;
            l.y -= linesContainer.y;
        });

        const spineContainer = scene.add.spineContainer(0, 0);
        leftAssets.add(spineContainer);

        const logoContainer = scene.add.container();
        this.add(logoContainer);

        const logoSpineContainer = scene.add.spineContainer(0, 0);
        logoContainer.add(logoSpineContainer);
        const logo = scene.add.image(0, 0, "splashLogo");
        logoContainer.add(logo);
        logoContainer.setSize(logo.width, logo.height);
        const stars = this.stars = scene.add.spine(-30, 30, "lmu-logo-stars");
        logoSpineContainer.add(stars);
        stars.play("idle", true);

        const characters = this.characters = scene.add.spine(780, 600, "splash-characters");
        spineContainer.add(characters);

        const tnt = scene.add.spine(380, 150, "powerup-tntcrate-sky");
        tnt.play("idle", true);
        spineContainer.add(tnt);
        tnt.scale = 0.25;

        const loadMeter = this.loadMeter = scene.add.container();
        this.add(loadMeter);

        const meterBacker = scene.add.image(0, 0, "loadbar");
        loadMeter.add(meterBacker);
        loadMeter.setSize(meterBacker.width, meterBacker.height);

		const meterFill = (this.meterFill = scene.add.nineSlicedSprite(0, 0, "loadbarFill", meterBacker.width - 8, 20, 10, 11, 10, 11, null, false));
		loadMeter.add(meterFill);
        meterFill.setOrigin(0, 0.5);
        
        const text = scene.add.text(0, 0, Strings.get("LoadingAssets"), {
            color: GameColors.WHITE,
            fontSize: "38px",
            fontFamily: Fonts.LuckiestGuy,
        });
        text.setOrigin(0.5);
        LayoutUtils.scaleToMaxWidth(text, 230);
        loadMeter.add(text);

        loadMeter.add([meterBacker, meterFill, text]);

        if (!showLoadMeter) loadMeter.visible = false;

        this._onResize = (): void => {
            const scale = Math.min(scene.viewport.width / 1920, 1);
            const adjust = 700 - 700 * scale;
            scene.alignIn.Center(loadBG, scene.viewport, adjust);
            leftAssets.x = -adjust * 1;
            logo.setScale(1 - (1 - scale) * 1.5);
            scene.alignIn.TopRight(logoContainer, scene.viewport, -20 + adjust * 0.8, -20);
            scene.alignIn.BottomCenter(loadMeter, scene.viewport, 0, -30);
            scene.alignIn.LeftCenter(meterFill, meterBacker, -4, 25);
            scene.alignIn.Center(text, meterBacker, 0, -16);
        };
        this.resize();

        this.onUpdate();

        this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this);

        this.buildComplete = new Promise(r => this.once(LoadingContainer.BUILD_COMPLETE, r));

        this.scene.tweens.add({
            targets: this.loadMeter,
            y: this.loadMeter.y,
            duration: 200,
            ease: Phaser.Math.Easing.Back.Out,
        });
        this.loadMeter.y += 100;

        if (intro) {
            characters.play("build-in", false);
            characters.on("complete", () => characters.play("idle", true));

            tnt.x += 400;
            tnt.y += 400;
            tnt.scale = 0;
            this.scene.tweens.add({
                targets: tnt,
                scale: 0.25,
                x: tnt.x - 400,
                y: tnt.y - 400,
                ease: Phaser.Math.Easing.Quadratic.Out,
                duration: 400,
                delay: 200,
            });

            logoContainer.angle = -12;
            logoContainer.scale = 0;
            this.scene.tweens.add({
                targets: logoContainer,
                scale: 1,
                angle: 0,
                ease: Phaser.Math.Easing.Back.Out,
                duration: 400,
                delay: 400,
            });

            linesContainer.scale = 0;
            this.scene.tweens.add({
                targets: linesContainer,
                scale: 1,
                angle: 0,
                ease: Phaser.Math.Easing.Quadratic.Out,
                duration: 400,
                delay: 100,
            });

            this.scene.time.delayedCall(800, () => this.emit(LoadingContainer.BUILD_COMPLETE));
        }
        else {
            characters.play("idle", true);
            this.emit(LoadingContainer.BUILD_COMPLETE);
        }

        if (state) {
            characters.state.tracks.forEach(t => t.trackTime = state.charactersTime);
            stars.state.tracks.forEach(t => t.trackTime = state.starsTime);
        }
    }

    update(_t: number, dt: number): void {
        dt /= 1000;
        this._percent -= (this._percent - this._targetPercent) * 8 * dt;
        this.lines.forEach(l => l.update(dt));
        this.onUpdate();
    }

    onUpdate(): void {
        if (this._timer < 10) {
            this._timer++;
        }
    }

    tweenOutMeter(onComplete: () => void): void {
        this.scene.tweens.add({
            targets: this.loadMeter,
            y: this.loadMeter.y + 100,
            duration: 200,
            ease: Phaser.Math.Easing.Back.In,
            onComplete: onComplete,
        });
    }

    setLoadPercent(percent: number): void {
        if (isNaN(percent)) percent = 0; // nothing to load = 0% progress
        if (this._timer < 10 && percent == 1) percent = 0; // prevent percent jumping straight to 1 for first frame
        this._targetPercent = Math.max(percent, this._targetPercent); //only forward!
        this.meterFill.scaleX = percent;
        this.onUpdate();
    }

    setLoadComplete(): void {
        this._targetPercent = 1;
        this._percent = 1;
        this.onUpdate();
    }

    resize(): void {
        this._onResize();
    }

    shutdown(): void {
        this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);
        super.shutdown();
    }
}

class AlphaFade extends Component {
    timer = 0;

    constructor(obj: GameObject, timer: number) {
        super(obj);
        this.timer = timer;
    }

    public update(dt: number): void {
        this.timer += dt;

        const t = (Math.sin(this.timer * (Math.PI * 2) * 0.6) + 1) / 2;
        this.obj.alpha = Phaser.Math.Linear(1, 0.45, t);
    }
}